import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Delete, Edit, Refresh } from '@mui/icons-material';
import { format } from 'date-fns';

import supportChatService from '../../services/supportChatService';
import { hasEndpointAccess } from '../../utils/rbac';
import CustomPagination from '../../components/common/CustomPagination';


const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return format(new Date(value), 'yyyy-MM-dd HH:mm');
  } catch (error) {
    return value;
  }
};

const addSerialNumbers = (data, page, pageSize) =>
  (data || []).map((row, idx) => ({
    ...row,
    sn: page * pageSize + idx + 1,
  }));


export default function SupportKnowledgePage() {
  const user = useSelector((state) => state.auth.user);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [entries, setEntries] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    is_active: '',
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  const [formData, setFormData] = useState({
    key: '',
    title: '',
    content: '',
    is_active: true,
  });

  const canCreate = useMemo(() => hasEndpointAccess(user, 'admin-support-chat-knowledge-list', 'POST'), [user]);
  const canUpdate = useMemo(() => hasEndpointAccess(user, 'admin-support-chat-knowledge-detail', 'PATCH'), [user]);
  const canDelete = useMemo(() => hasEndpointAccess(user, 'admin-support-chat-knowledge-detail', 'DELETE'), [user]);

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(pageSize.toString());
      return;
    }
    setPageSize(Number(value));
    setPage(0);
    setCustomRowsActive(false);
  };

  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setPageSize(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page: page + 1,
        page_size: pageSize,
      };
      if (filters.search.trim()) {
        params.search = filters.search.trim();
      }
      if (filters.is_active !== '') {
        params.is_active = filters.is_active;
      }

      const data = await supportChatService.listKnowledge(params);
      if (data?.results) {
        setEntries(data.results);
        setTotalCount(data.count || data.results.length);
      } else if (Array.isArray(data)) {
        setEntries(data);
        setTotalCount(data.length);
      } else {
        setEntries([]);
        setTotalCount(0);
      }
    } catch (err) {
      setEntries([]);
      setTotalCount(0);
      setError(err?.response?.data?.detail || 'Failed to load support knowledge.');
    } finally {
      setLoading(false);
    }
  }, [filters.is_active, filters.search, page, pageSize]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleOpenDialog = (entry = null) => {
    if (entry) {
      setEditingEntry(entry);
      setFormData({
        key: entry.key || '',
        title: entry.title || '',
        content: entry.content || '',
        is_active: entry.is_active !== undefined ? entry.is_active : true,
      });
    } else {
      setEditingEntry(null);
      setFormData({
        key: '',
        title: '',
        content: '',
        is_active: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEntry(null);
  };

  const handleSubmit = async () => {
    if (!formData.key.trim() || !formData.title.trim() || !formData.content.trim()) {
      showSnackbar('Please fill in key, title, and content.', 'error');
      return;
    }

    try {
      if (editingEntry) {
        if (!canUpdate) {
          showSnackbar('You do not have permission to update support knowledge.', 'error');
          return;
        }
        await supportChatService.updateKnowledge(editingEntry.id, {
          key: formData.key.trim(),
          title: formData.title.trim(),
          content: formData.content,
          is_active: formData.is_active,
        });
        showSnackbar('Knowledge updated.');
      } else {
        if (!canCreate) {
          showSnackbar('You do not have permission to create support knowledge.', 'error');
          return;
        }
        await supportChatService.createKnowledge({
          key: formData.key.trim(),
          title: formData.title.trim(),
          content: formData.content,
          is_active: formData.is_active,
        });
        showSnackbar('Knowledge created.');
      }

      handleCloseDialog();
      fetchEntries();
    } catch (err) {
      showSnackbar(err?.response?.data?.detail || 'Failed to save knowledge entry.', 'error');
    }
  };

  const handleDelete = async (entry) => {
    if (!canDelete) {
      showSnackbar('You do not have permission to delete support knowledge.', 'error');
      return;
    }
    if (!window.confirm(`Delete knowledge "${entry.title}" (${entry.key})?`)) {
      return;
    }
    try {
      await supportChatService.deleteKnowledge(entry.id);
      showSnackbar('Knowledge deleted.');
      fetchEntries();
    } catch (err) {
      showSnackbar(err?.response?.data?.detail || 'Failed to delete knowledge entry.', 'error');
    }
  };

  const columns = useMemo(
    () => [
      { field: 'sn', headerName: 'S/N', width: 70, sortable: false },
      { field: 'title', headerName: 'Title', minWidth: 220, flex: 1 },
      { field: 'key', headerName: 'Key', minWidth: 180, flex: 0.8 },
      {
        field: 'is_active',
        headerName: 'Active',
        minWidth: 110,
        flex: 0.5,
        renderCell: ({ value }) => (
          <Chip label={value ? 'YES' : 'NO'} color={value ? 'success' : 'default'} size="small" sx={{ fontWeight: 700 }} />
        ),
      },
      {
        field: 'updated_at',
        headerName: 'Updated',
        minWidth: 160,
        flex: 0.8,
        renderCell: ({ value }) => formatDateTime(value),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 140,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5}>
            {canUpdate && (
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => handleOpenDialog(row)}>
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    [canDelete, canUpdate, handleDelete]
  );

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  Support Knowledge
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Used by the Support Chat AI prompt to answer questions accurately.
                </Typography>
              </Box>
            </Grid>

            <Grid
              size={{ xs: 12, md: 4 }}
              sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1 }}
            >
              <Tooltip title="Refresh">
                <IconButton onClick={fetchEntries} disabled={loading}>
                  <Refresh />
                </IconButton>
              </Tooltip>
              {canCreate && (
                <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog(null)}>
                  Add Entry
                </Button>
              )}
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                label="Search (title or key)"
                value={filters.search}
                onChange={(e) => {
                  setPage(0);
                  setFilters((prev) => ({ ...prev, search: e.target.value }));
                }}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                select
                label="Active"
                value={filters.is_active}
                onChange={(e) => {
                  setPage(0);
                  setFilters((prev) => ({ ...prev, is_active: e.target.value }));
                }}
                SelectProps={{ native: true }}
                fullWidth
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </TextField>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ py: 10, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ width: '100%', overflowX: 'auto', minHeight: 520 }}>
              <DataGrid
                autoHeight
                rows={addSerialNumbers(entries, page, pageSize)}
                columns={columns}
                getRowId={(row) => row.id}
                loading={loading}
                hideFooterPagination
                hideFooter
                disableSelectionOnClick
                sx={{
                  border: 'none',
                  minWidth: 900,
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: (theme) => theme.palette.primary.main,
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: '#fff',
                    fontWeight: 700,
                  },
                  '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                    color: '#fff',
                  },
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={page}
          totalPages={totalPages}
          rowsPerPage={pageSize}
          onPageChange={setPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingEntry ? 'Edit Knowledge' : 'Add Knowledge'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Key (unique)"
              value={formData.key}
              onChange={(e) => setFormData((prev) => ({ ...prev, key: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Content"
              value={formData.content}
              onChange={(e) => setFormData((prev) => ({ ...prev, content: e.target.value }))}
              fullWidth
              multiline
              minRows={8}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!formData.is_active}
                  onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.checked }))}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
